/**
 * Charts for the Energy Efficiency details panel: BAU vs efficiency-adjusted demand, and efficiency by policy.
 */
import React, { useMemo } from 'react';
import {
  CategoryScale,
  Chart as ChartJS,
  ChartOptions,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
  BarElement,
  BarController,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import type { EfficiencyContributions } from '../../services/buildingsEfficiencyModel';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  BarController,
  Tooltip,
  Legend
);

const CHART_YEAR_MIN = 2023;
const CHART_YEAR_MAX = 2045;
const numFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

export interface EnergyEfficiencyOverTimeData {
  years: number[];
  bauTherms: number[];
  bauKwh: number[];
  effTherms: number[];
  effKwh: number[];
}

export interface EnergyEfficiencyOverTimeChartProps {
  data: EnergyEfficiencyOverTimeData | null;
}

export const EnergyEfficiencyOverTimeChart: React.FC<EnergyEfficiencyOverTimeChartProps> = ({ data }) => {
  const { labels, bauTherms, bauKwh, effTherms, effKwh } = useMemo(() => {
    if (!data?.years?.length) {
      return { labels: [] as string[], bauTherms: [], bauKwh: [], effTherms: [], effKwh: [] };
    }
    const years = data.years.filter((y) => y >= CHART_YEAR_MIN && y <= CHART_YEAR_MAX);
    const idx = (y: number) => data.years.indexOf(y);
    return {
      labels: years.map(String),
      bauTherms: years.map((y) => data.bauTherms[idx(y)] ?? 0),
      bauKwh: years.map((y) => data.bauKwh[idx(y)] ?? 0),
      effTherms: years.map((y) => data.effTherms[idx(y)] ?? 0),
      effKwh: years.map((y) => data.effKwh[idx(y)] ?? 0),
    };
  }, [data]);

  const chartData = useMemo(
    () => ({
      labels,
      datasets: [
        {
          label: 'BAU Therms',
          data: bauTherms,
          borderColor: 'rgb(234, 88, 12)',
          backgroundColor: 'rgba(234, 88, 12, 0.1)',
          borderDash: [6, 4],
          yAxisID: 'yTherms',
          tension: 0.2,
          pointRadius: 2,
        },
        {
          label: 'With Efficiency Therms',
          data: effTherms,
          borderColor: 'rgb(251, 146, 60)',
          backgroundColor: 'rgba(251, 146, 60, 0.1)',
          yAxisID: 'yTherms',
          tension: 0.2,
          pointRadius: 2,
        },
        {
          label: 'BAU Electricity',
          data: bauKwh,
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          borderDash: [6, 4],
          yAxisID: 'yKwh',
          tension: 0.2,
          pointRadius: 2,
        },
        {
          label: 'With Efficiency Electricity',
          data: effKwh,
          borderColor: 'rgb(96, 165, 250)',
          backgroundColor: 'rgba(96, 165, 250, 0.1)',
          yAxisID: 'yKwh',
          tension: 0.2,
          pointRadius: 2,
        },
      ],
    }),
    [labels, bauTherms, bauKwh, effTherms, effKwh]
  );

  const options: ChartOptions<'line'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { position: 'bottom' as const },
        tooltip: {
          callbacks: {
            label: (ctx) => {
              const v = ctx.parsed.y;
              const label = ctx.dataset.label || '';
              if (label.includes('Therms')) return `${label}: ${numFormatter.format(Number(v))}`;
              return `${label}: ${numFormatter.format(Number(v))} kWh`;
            },
          },
        },
      },
      scales: {
        x: { title: { display: true, text: 'Year' } },
        yTherms: {
          type: 'linear',
          position: 'left',
          title: { display: true, text: 'Therms' },
          min: 0,
        },
        yKwh: {
          type: 'linear',
          position: 'right',
          title: { display: true, text: 'kWh' },
          min: 0,
          grid: { drawOnChartArea: false },
        },
      },
    }),
    []
  );

  if (!data || labels.length === 0) return null;
  const hasData = effTherms.some((v) => v > 0) || effKwh.some((v) => v > 0) || bauTherms.some((v) => v > 0) || bauKwh.some((v) => v > 0);
  if (!hasData) return null;

  return (
    <div className="building-electrification-chart-card" style={{ marginTop: 16 }}>
      <h5>Energy Efficiency Over Time</h5>
      <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: 8 }}>BAU demand vs efficiency-adjusted demand (therms and kWh).</p>
      <div className="building-electrification-chart-container" style={{ height: 280 }}>
        <Line data={chartData} options={options} />
      </div>
    </div>
  );
};

export interface EfficiencyByPolicyChartProps {
  contributions: EfficiencyContributions | null;
  yearLabel?: string;
}

const POLICY_LABELS: Record<keyof EfficiencyContributions, string> = {
  weatherization: 'Weatherization',
  residentialReach: 'Residential Reach',
  commercialReach: 'Commercial Reach',
  demandResponse: 'Demand Response',
  other: 'Other / Unmodeled',
};

const POLICY_COLORS: Record<keyof EfficiencyContributions, string> = {
  weatherization: 'rgb(34, 197, 94)',
  residentialReach: 'rgb(59, 130, 246)',
  commercialReach: 'rgb(168, 85, 247)',
  demandResponse: 'rgb(251, 146, 60)',
  other: 'rgb(107, 114, 128)',
};

export const EfficiencyByPolicyChart: React.FC<EfficiencyByPolicyChartProps> = ({ contributions, yearLabel = '2045' }) => {
  const { labels, values, colors } = useMemo(() => {
    if (!contributions) return { labels: [] as string[], values: [] as number[], colors: [] as string[] };
    const keys: (keyof EfficiencyContributions)[] = ['weatherization', 'residentialReach', 'commercialReach', 'demandResponse', 'other'];
    const labels = keys.map((k) => POLICY_LABELS[k]);
    const values = keys.map((k) => contributions[k] ?? 0);
    const colors = keys.map((k) => POLICY_COLORS[k]);
    return { labels, values, colors };
  }, [contributions]);

  const chartData = useMemo(
    () => ({
      labels,
      datasets: [
        {
          label: `Marginal reduction (kWh equivalent)`,
          data: values,
          backgroundColor: colors,
          borderColor: colors.map((c) => c.replace('rgb', 'rgba').replace(')', ', 0.9)').replace(' ', ', ')),
          borderWidth: 1,
        },
      ],
    }),
    [labels, values, colors]
  );

  const options: ChartOptions<'bar'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: 'y' as const,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx) => `${ctx.label}: ${numFormatter.format(Number(ctx.parsed.x))} kWh equivalent`,
          },
        },
      },
      scales: {
        x: {
          title: { display: true, text: 'Reduction (kWh equivalent, 1 therm = 29.3 kWh)' },
          min: 0,
        },
        y: { title: { display: true, text: 'Policy' } },
      },
    }),
    []
  );

  if (!contributions || labels.length === 0) return null;
  const hasData = values.some((v) => Math.abs(v) >= 1);
  if (!hasData) return null;

  return (
    <div className="building-electrification-chart-card" style={{ marginTop: 16 }}>
      <h5>Marginal Contribution to Energy Reduction ({yearLabel})</h5>
      <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: 8 }}>Bars show the reduction from each policy individually; values are not additive due to overlapping effects.</p>
      <div className="building-electrification-chart-container" style={{ height: 220 }}>
        <Bar data={chartData} options={options} />
      </div>
    </div>
  );
};
